<template>
  <div class="chart-container">
    <!-- 顶部头部区域 -->
    <div class="header">
      <div class="title-wrap">
        <div class="main-title">
          <i class="icon-chart"></i> 包裹量趋势变化
        </div>
        <div class="sub-title">{{ tips }}</div>
      </div>
      <!-- 右侧切换按钮 -->
      <div class="button-group">
        <div class="btn" :class="{active: cycleType === 'DAY'}" @click="setCycleType('DAY')">日周期</div>
        <div class="btn" :class="{active: cycleType === 'WEEK'}" @click="setCycleType('WEEK')">周周期</div>
        <div class="btn" :class="{active: cycleType === 'MONTH'}" @click="setCycleType('MONTH')">月周期</div>
      </div>
    </div>

    <!-- ECharts 图表容器 -->
    <div ref="chartRef" class="echarts-box"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, shallowRef } from 'vue';
import * as echarts from 'echarts';
import { computed, useHostAppInstance } from '@zto/zpage'
import { getLineInterval, getMinMax } from '@/components/tdl/data/package/utils'

const props = withDefaults(
  defineProps<{
    cycleType?: string
  }>(),
  {}
)
const tips = computed(() => {
  if ('DAY' === props.cycleType) {
    return '日周期-近30日包裹趋势'
  }
  if ('WEEK' === props.cycleType) {
    return '周周期-近8周包裹趋势'
  }
  if ('MONTH' === props.cycleType) {
    return '月周期-近6个月包裹趋势'
  }
  return ''

})
const app = useHostAppInstance()
const emit = defineEmits(['changeCycleType'])

const queryLineData = async (params: any) => {
  console.log('===doSearch PackageLineChart:', JSON.stringify(params))
  try {
    const resp = await app.request({
      api: '/sys/admin/statistics/page-statistics/package-volume-trend',
      params: params
    })
    // console.log('====doSearch resp:', JSON.stringify(resp))
    if (resp) {
      const xDatas = resp.periodList || []
      const yDatas = resp.parcelNumList || []
      drawLine(xDatas, yDatas)
    }
  } catch (e) {

  }

}
// 获取 DOM 引用
const chartRef = ref();
// 保存 echarts 实例 (使用 shallowRef 避免 vue 深度代理导致性能问题和内部报错)
const chartInstance = shallowRef();

// 初始化图表的方法
const initChart = () => {
  console.log('====图表初始化')
  chartInstance.value = echarts.init(chartRef.value);
};

const setCycleType = (type: string) => {
  emit('changeCycleType', type)
}
defineExpose({
  queryLineData, setCycleType, initChart
})



const drawLine = (xAxisData: any, seriesData: any) => {
  const yMinMax = getMinMax(seriesData)
  const lineInterval = getLineInterval(yMinMax)
  const option = {
    // 设置绘图区域边距
    grid: {
      top: '15%',
      left: '3%',
      right: '3%',
      bottom: '5%',
      containLabel: true
    },
    // 提示框组件 (Hover时的弹窗)
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(13, 27, 49, 0.9)', // 还原暗色背景
      borderColor: '#00d2ff', // 边框颜色
      textStyle: { color: '#fff' },
      padding: [10, 15],
      axisPointer: {
        type: 'line',
        lineStyle: {
          color: '#ffffff',
          type: 'dashed', // 虚线指示器
          opacity: 0.5
        }
      }
    },
    // X轴配置
    xAxis: {
      type: 'category',
      boundaryGap: false, // 让折线两端贴边
      data: xAxisData,
      axisLine: {
        lineStyle: { color: '#2b3d5b' } // X轴轴线颜色
      },
      axisLabel: {
        color: '#8a9ab9', // 底部文字颜色
        fontSize: 12
      },
      axisTick: { show: false } // 隐藏刻度小短线
    },
    // Y轴配置
    yAxis: {
      type: 'value',
      min: yMinMax.min,
      max: yMinMax.max,
      interval: lineInterval, // 每隔300画一条线
      axisLabel: {
        color: '#8a9ab9',
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: 'rgba(43, 61, 91, 0.5)', // 还原背景中的横向分割线
          type: 'solid'
        }
      }
    },
    // 核心数据系列配置
    series: [
      {
        name: '包裹量',
        type: 'line',
        smooth: true, // 开启平滑曲线 (极其关键)
        showSymbol: false, // 正常状态不显示圆点
        symbol: 'circle', // Hover时的圆点形状
        symbolSize: 8,
        itemStyle: {
          color: '#00d2ff', // 线条和圆点的颜色 (青蓝色)
        },
        lineStyle: {
          width: 2,
          shadowColor: 'rgba(0, 210, 255, 0.3)',
          shadowBlur: 10
        },
        // 区域填充样式 (渐变色核心)
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {
              offset: 0,
              color: 'rgba(0, 210, 255, 0.4)' // 顶部颜色
            },
            {
              offset: 1,
              color: 'rgba(0, 210, 255, 0.0)' // 底部渐变到透明
            }
          ])
        },
        data: seriesData
      }
    ]
  };

  chartInstance.value.setOption(option);
}

// 监听窗口大小变化，使图表自适应响应式
const handleResize = () => {
  if (chartInstance.value) {
    chartInstance.value.resize();
  }
};

onMounted(() => {
  console.log('====onMounted:')
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  if (chartInstance.value) {
    chartInstance.value.dispose();
  }
});
</script>

<style scoped>
/* 容器整体暗色背景和布局 */
.chart-container {
  width: 100%;
  height: 400px;
  background-color: #0b1426; /* 暗蓝背景色 */
  border-radius: 8px;
  padding: 20px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  font-family: 'Microsoft YaHei', sans-serif;
  border: 1px solid #1a2a46; /* 外边框 */
}

/* 头部布局 */
.header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
}

.title-wrap {
  display: flex;
  flex-direction: column;
}

.main-title {
  color: #ffffff;
  font-size: 16px;
  font-weight: bold;
  display: flex;
  align-items: center;
}

/* 用伪元素模拟标题前面的小图标 */
.main-title::before {
  content: '';
  display: inline-block;
  width: 14px;
  height: 14px;
  background-color: #00d2ff;
  mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M3 3v18h18v-2H5V3H3zm6 14l-4-4 4-4 4 4 4-4 4 4v4h-4v-4l-4 4-4-4v4H9z'/%3E%3C/svg%3E") no-repeat center / contain;
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M3 3v18h18v-2H5V3H3zm6 14l-4-4 4-4 4 4 4-4 4 4v4h-4v-4l-4 4-4-4v4H9z'/%3E%3C/svg%3E") no-repeat center / contain;
  margin-right: 8px;
}

.sub-title {
  color: #8a9ab9;
  font-size: 12px;
  margin-top: 6px;
}

/* 按钮组样式 */
.button-group {
  display: flex;
  background-color: #101e36;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #1a2a46;
}

.btn {
  padding: 6px 16px;
  color: #8a9ab9;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s;
}

.btn.active {
  background-color: #00aeff; /* 亮蓝色选中态 */
  color: #ffffff;
}

/* ECharts 图表所占区域 */
.echarts-box {
  flex: 1;
  width: 100%;
}
</style>
